import {createRecipe} from './recipe.js';
import {createRecipeItem,createRecipeItemPreparation} from './recipe-item.js';
import {
  createRecipePlan,
  createRecipeStage,
  createRecipeStageItemTarget,
  createRecipeStageTarget,
  createRecipeWood
} from './recipe-process.js';
import {privateRecipeCustomCodeFromProductId} from './private-product-code.js';

const ELIGIBLE=new Set(['planned','completed']);

function required(value,label,max=160){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalText(value,max=8000){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>max)throw new TypeError('Text value is too long');
  return text;
}

function nextId(idFactory,kind,sourceId){
  if(typeof idFactory!=='function')throw new TypeError('idFactory is required');
  const id=String(idFactory(kind,sourceId)||'').trim();
  if(!id)throw new TypeError('idFactory must return an id');
  return id;
}

function recipeIdOrGenerated(value){
  const id=String(value??'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(!generated)throw new TypeError('recipeId is required when crypto.randomUUID is unavailable');
  return generated;
}

function woodKeyFor(wood,resolver){
  if(wood.woodKey)return wood.woodKey;
  if(typeof resolver==='function'){
    const key=String(resolver(wood)||'').trim().toLowerCase();
    if(key)return key;
  }
  throw new TypeError('PRIVATE_RECIPE_WOOD_CODE_REQUIRED');
}

export function createPrivateRecipeFromSession(bundle,{
  userId,
  recipeId=null,
  title=null,
  description=null,
  locale='pl',
  idFactory=(kind)=>globalThis.crypto?.randomUUID?.(),
  resolveWoodKey=null,
  now=()=>new Date().toISOString()
}={}){
  if(!bundle?.session)throw new TypeError('Session bundle is required');
  const owner=required(userId,'userId',128);
  const source=bundle.session;
  if(source.userId!==owner)throw new TypeError('SESSION_OWNER_MISMATCH');
  if(!ELIGIBLE.has(source.status))throw new TypeError('PRIVATE_RECIPE_SOURCE_NOT_ELIGIBLE');

  const id=recipeIdOrGenerated(recipeId);
  const createdAt=now();
  const recipe=createRecipe({
    id,
    type:'private',
    visibility:'private',
    authorUserId:owner,
    authorName:null,
    sourceName:null,
    sourceUrl:null,
    defaultLocale:locale,
    translations:[{
      locale,
      title:required(title||source.title||'Moja receptura','title',160),
      description:optionalText(description)
    }],
    version:1,
    createdAt,
    updatedAt:createdAt
  });

  const itemIdMap=new Map();
  const items=(bundle.items||[]).filter(item=>!item.deletedAt).map(item=>{
    const recipeItemId=nextId(idFactory,'item',item.id);
    itemIdMap.set(item.id,recipeItemId);
    const input={
      id:recipeItemId,
      recipeId:id,
      name:item.nameSnapshot,
      referenceQuantity:item.quantity,
      referenceMass:item.mass,
      massUnit:item.massUnit,
      scaleMode:'proportional',
      sortOrder:item.sortOrder
    };
    if(item.catalogueProductCode)input.catalogueProductCode=item.catalogueProductCode;
    else input.customProductCode=privateRecipeCustomCodeFromProductId(item.customProductId);
    return createRecipeItem(input);
  });
  if(items.length<1)throw new TypeError('Private Recipe requires at least one active item');

  const preparations=(bundle.preparations||[]).map(prep=>{
    const recipeItemId=itemIdMap.get(prep.itemId);
    if(!recipeItemId)throw new TypeError('PRIVATE_RECIPE_PREPARATION_ITEM_MISMATCH');
    return createRecipeItemPreparation({
      recipeItemId,
      curingMethodKey:prep.curingMethodKey,
      curingMethodCustom:prep.curingMethodCustom,
      curingDurationMinutes:prep.curingDurationMinutes,
      curingNotes:prep.curingNotes,
      marinadeNotes:prep.marinadeNotes,
      spicesNotes:prep.spicesNotes,
      dryingDurationMinutes:prep.dryingDurationMinutes,
      dryingNotes:prep.dryingNotes,
      preparationDurationMinutes:prep.preparationDurationMinutes,
      preparationNotes:prep.preparationNotes
    });
  });

  const woods=(bundle.woods||[]).filter(wood=>!wood.deletedAt).map(wood=>createRecipeWood({
    id:nextId(idFactory,'wood',wood.id),
    recipeId:id,
    woodKey:woodKeyFor(wood,resolveWoodKey),
    name:wood.nameSnapshot,
    sharePercent:wood.sharePercent,
    sortOrder:wood.sortOrder
  }));

  const stageIdMap=new Map();
  const stages=(bundle.stages||[]).filter(stage=>!stage.deletedAt).map(stage=>{
    const recipeStageId=nextId(idFactory,'stage',stage.id);
    stageIdMap.set(stage.id,recipeStageId);
    return createRecipeStage({
      id:recipeStageId,
      recipeId:id,
      typeKey:stage.typeKey,
      nameOverride:stage.nameOverride,
      instructions:stage.instructions,
      sortOrder:stage.sortOrder
    });
  });
  if(stages.length<1)throw new TypeError('Private Recipe requires at least one active stage');

  const stageTargets=(bundle.stageTargets||[]).map(target=>{
    const recipeStageId=stageIdMap.get(target.stageId);
    if(!recipeStageId)throw new TypeError('PRIVATE_RECIPE_STAGE_TARGET_MISMATCH');
    return createRecipeStageTarget({
      recipeStageId,
      targetChamberCelsius:target.targetChamberCelsius,
      minChamberCelsius:target.minChamberCelsius,
      maxChamberCelsius:target.maxChamberCelsius,
      plannedDurationMinutes:target.plannedDurationMinutes
    });
  });

  const stageItemTargets=(bundle.itemTargets||bundle.stageItemTargets||[]).map(target=>{
    const recipeStageId=stageIdMap.get(target.stageId);
    const recipeItemId=itemIdMap.get(target.itemId);
    if(!recipeStageId||!recipeItemId)throw new TypeError('PRIVATE_RECIPE_STAGE_ITEM_TARGET_MISMATCH');
    return createRecipeStageItemTarget({
      recipeStageId,
      recipeItemId,
      targetInternalCelsius:target.targetInternalCelsius
    });
  });

  return Object.freeze({
    sourceSessionId:source.id,
    plan:createRecipePlan({
      recipe,
      items,
      preparations,
      woods,
      stages,
      stageTargets,
      stageItemTargets
    })
  });
}

export function editPrivateRecipePlan(plan,{
  title,
  description,
  items,
  preparations,
  woods,
  stages,
  stageTargets,
  stageItemTargets,
  updatedAt=new Date().toISOString()
}={}){
  const current=createRecipePlan(plan);
  if(current.recipe.type!=='private'||current.recipe.visibility!=='private'){
    throw new TypeError('Only private Recipes can be edited through this flow');
  }
  const locale=current.recipe.defaultLocale;
  const translations=current.recipe.translations.map(item=>
    item.locale===locale
      ?{locale,title:title??item.title,description:description===undefined?item.description:description}
      :item
  );
  const recipe=createRecipe({
    ...current.recipe,
    translations,
    version:current.recipe.version+1,
    updatedAt
  });
  return createRecipePlan({
    recipe,
    items:items??current.items,
    preparations:preparations??current.preparations,
    woods:woods??current.woods,
    stages:stages??current.stages,
    stageTargets:stageTargets??current.stageTargets,
    stageItemTargets:stageItemTargets??current.stageItemTargets
  });
}
